import type { BandPosition } from './bandTypes';

/**
 * 저점매수 보유 종목 정렬. (2026-09-16 사용자 요청)
 *
 * "기본은 가장 최근에 매수한 것이 맨 위. 1차·2차가 있으면 2차가 기준이다.
 *  정렬 필터는 둘 - 매수일과 수익률."
 *
 * 🔴 매수일 = 마지막 매수 체결 시각이다. 첫 매수일이 아니다
 *   A 를 9/4 1차 · 9/10 2차, B 를 9/8 1차로 샀으면 A(9/10)가 B(9/8)보다 위다.
 *   2차를 샀다는 것은 방금 그 종목에 돈을 더 넣었다는 뜻이라 "방금 뭘 샀나" 에 답하는 순서다.
 *   체결 기록이 비어 있으면(있을 수 없지만) 첫 매수 시각으로 대신한다.
 *
 * 🔴 수익률 = 서버가 준 평단 대비 수익률(unrealizedPercent)이다
 *   화면 옆에 찍히는 숫자와 같은 값으로 정렬해야 "정렬했는데 순서가 이상하다" 가 안 생긴다.
 *   시세가 없어 null 인 종목은 방향과 무관하게 맨 아래다.
 */

export type PositionSortKey = 'buy' | 'return';

export interface PositionSort {
  key: PositionSortKey;
  dir: 'desc' | 'asc';
}

/** 기본 정렬 - 최근 매수가 맨 위 */
export const DEFAULT_POSITION_SORT: PositionSort = { key: 'buy', dir: 'desc' };

/**
 * 마지막 매수 체결 시각(ISO 문자열).
 * 같은 형식("2026-09-15T09:00:00")끼리라 문자열 비교가 곧 시각 비교다.
 */
export function lastBuyAt(position: BandPosition): string {
  const buys = position.fills.filter((fill) => fill.type === 'BUY').map((fill) => fill.filledAt);
  if (buys.length === 0) {
    return position.firstBuyAt;
  }
  return buys.reduce((latest, at) => (at > latest ? at : latest));
}

export function sortPositions(positions: BandPosition[], sort: PositionSort): BandPosition[] {
  const sign = sort.dir === 'desc' ? -1 : 1;
  return [...positions].sort((a, b) => {
    if (sort.key === 'return') {
      // null 은 방향과 무관하게 뒤로
      if (a.unrealizedPercent === null || b.unrealizedPercent === null) {
        if (a.unrealizedPercent === b.unrealizedPercent) {
          return b.id - a.id;
        }
        return a.unrealizedPercent === null ? 1 : -1;
      }
      const diff = a.unrealizedPercent - b.unrealizedPercent;
      return diff !== 0 ? sign * diff : b.id - a.id;
    }
    const at = lastBuyAt(a);
    const bt = lastBuyAt(b);
    if (at !== bt) {
      return sign * (at < bt ? -1 : 1);
    }
    return b.id - a.id;
  });
}

/** 같은 필터를 다시 누르면 방향이 바뀌고, 다른 필터를 누르면 그 필터의 내림차순부터 */
export function nextPositionSort(sort: PositionSort, key: PositionSortKey): PositionSort {
  if (sort.key === key) {
    return { key, dir: sort.dir === 'desc' ? 'asc' : 'desc' };
  }
  return { key, dir: 'desc' };
}

/** 버튼 글자. 켜진 필터는 지금 방향을 말하고, 꺼진 필터는 이름만 */
export function positionSortLabel(key: PositionSortKey, sort: PositionSort): string {
  const active = sort.key === key;
  if (key === 'buy') {
    if (!active) {
      return '매수일';
    }
    return sort.dir === 'desc' ? '매수일 최신순 ↓' : '매수일 오래된순 ↑';
  }
  if (!active) {
    return '수익률';
  }
  return sort.dir === 'desc' ? '수익률 높은순 ↓' : '수익률 낮은순 ↑';
}
